/* =============================================================================
   VIEWS / ADMIN / OPTION-GROUPS-ADMIN — Optionssätze-Editor (S188-2)
   =============================================================================
   Unter System → Speisekarte → „Optionssätze". Verwaltet die First-Class-Gruppen
   (menu_categories kind option/extra mit role/config/group_key, phase_143) UND
   ihre Wahl-Items (menu_items itemType option/extra) — OHNE Meta-JSON-Gefummel.

   SYNC-VERTRAG (core/option-groups.js): beim Schreiben eines Wahl-Items werden
   category_id/subcategory_id + meta.optionGroup (option) bzw. meta.addonGroup
   (extra) IMMER auf die Gruppe gesetzt; meta.optionGroups[] (Reuse, S171 P5) wird
   erhalten/ergänzt, NIE zerstört. So liefern optionGroupsFromMenu +
   optionsByGroupFromMenu + addonsByGroupFromMenu vor/nach jedem Edit identisch —
   Konfigurator/Gast-Pfad bleiben unberührt (S188-3 wertet role/config aus).

   Non-destruktiv (Memory feedback_gg_non_destructive_render): Tippen speichert
   debounced ohne Re-Render (Fokus bleibt); nur strukturelle Aktionen (anlegen/
   löschen/sortieren/aktiv) zeichnen die betroffene Pane neu. owner/admin/manager-
   gated (RLS + requireEditPermission), Staff read-only. Inline-Handler via
   dom-bindings (kein window.func()). Kein DB-Touch (Spalten existieren, generischer
   Adapter). Hinter Feature-Flag ingredientsDb (wie der übrige Hub).
============================================================================ */

import { htmlEscape, showToast, requireEditPermission } from '../../utils/helpers.js';
import {
  OPTION_ROLES, roleLabel, listOptionGroups, rootForKind, itemsForGroup,
  buildGroupItemMeta, addReuseToMeta, removeReuseFromMeta, groupItemPayload,
  slugifyGroupKey, filterFilledRows, groupKindDefaults
} from '../../core/option-groups.js';
// S206 — Single-Zutat-Link (recipe_items) für Wahl-Items: wiederverwendet die
// bestehende Normalisierung (qty-Default, unit 'base') aus dem Rezept-Framing.
import { cleanIngredientRow } from '../../core/recipe-entity.js';

const KIND_SECTIONS = [
  { kind: 'option', title: 'Optionen', hint: 'Brötchen · Patty · Dressing — Auswahl-Sätze, aus denen ein Gericht pickt.', addLabel: '+ Options-Satz' },
  { kind: 'extra',  title: 'Extras',   hint: 'Toppings — Aufpreis-Zutaten, die ein Gast optional hinzufügt.', addLabel: '+ Extra-Satz' }
];

let _cats = [];
let _menu = [];
let _ingredients = [];   // S206 — Zutaten-Cache für den Inline-Picker (1× je Render geladen)
// S206 — Inline-Draft-State (Modul-State → überlebt strukturelle Re-Renders, kein
// Verlust der Fließband-Zeile). _itemDraft: eine Wahl-Item-Zeile je Gruppe;
// _groupDraft: das Options-Satz-Panel (Name + mehrere Item-Zeilen).
let _itemDraft = null;   // { groupId, name, ingredientId, price, available }
let _groupDraft = null;  // { kind, name, rows: [{ name, ingredientId, price }] }
const _saveTimers = {};
// S217 Cut 1 — welche Options-/Extra-Sätze sind aufgeklappt (per id). Modul-State
// (nicht DOM), damit ein Re-Render den Collapse-Zustand nicht zurücksetzt
// (gg-non-destructive-render). Default: alle eingeklappt (nur Kopfzeile).
const _expandedGroups = new Set();

function flagOn() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}
function storage() { return window.App && window.App.storage; }
function menuState() {
  const st = storage() && storage()._getInternalState && storage()._getInternalState();
  return (st && st.menu) || [];
}
function canEdit() {
  const auth = window.App && window.App.auth;
  return !auth || typeof auth.canEdit !== 'function' ? true : auth.canEdit('menu');
}
function _debouncedSave(key, fn, ms) {
  if (_saveTimers[key]) clearTimeout(_saveTimers[key]);
  _saveTimers[key] = setTimeout(() => { _saveTimers[key] = null; fn(); }, ms || 450);
}
function _group(id) { return listOptionGroups(_cats).find(g => g.id === id) || null; }
function _item(id) { return _menu.find(m => m.id === id) || null; }

/** Nährwert-/Rezept-Badges (kcal bzw. „Kein Rezept hinterlegt") je Wahl-Item
 *  asynchron füllen — best-effort, nutzt den bestehenden RPC-Helfer. */
function _fillNutri(itemIds) {
  if (typeof window.renderRecipeNutritionInto !== 'function') return;
  (itemIds || []).forEach(id => { try { window.renderRecipeNutritionInto(id); } catch (_) { /* best-effort */ } });
}
function _allItemIds() {
  return listOptionGroups(_cats).flatMap(g => itemsForGroup(_menu, g).map(x => x.item.id));
}

/* -------------------------------------------------------------------------- */
/* HTML-Builder                                                                */
/* -------------------------------------------------------------------------- */

function _roleSelectHtml(group, ro) {
  const opts = OPTION_ROLES.map(r =>
    `<option value="${r}"${r === group.role ? ' selected' : ''}>${htmlEscape(roleLabel(r))}</option>`).join('');
  return `<select class="og-role" ${ro ? 'disabled' : ''} onchange="ogGroupRole('${group.id}', this.value)" title="Rolle">${opts}</select>`;
}

function _itemRowHtml(group, entry, ro) {
  const it = entry.item;
  const reuse = entry.membership === 'reuse';
  const dim = it.available === false ? ' og-item-off' : '';
  const price = (Number(it.price) || 0).toString();
  const badge = reuse ? `<span class="og-badge" title="geliehen aus dem Heim-Satz">↔ geliehen</span>` : '';
  const nameInput = `<input class="og-item-name" type="text" value="${htmlEscape(it.name || '')}"
      ${(ro || reuse) ? 'disabled' : ''} oninput="ogItemRename('${it.id}', this.value)" placeholder="Name">`;
  const priceInput = `<span class="og-item-price-wrap">+<input class="og-item-price" type="number" step="0.10" min="0"
      value="${htmlEscape(price)}" ${(ro || reuse) ? 'disabled' : ''} oninput="ogItemPrice('${it.id}', this.value)"> €</span>`;
  const avail = `<label class="og-item-avail" title="verfügbar"><input type="checkbox" ${it.available !== false ? 'checked' : ''}
      ${ro ? 'disabled' : ''} onchange="ogItemAvailable('${it.id}', '${group.id}', this.checked)"> verfügbar</label>`;
  // S188-2 — Rezept-Link: das Wahl-Item verweist auf die dahinterliegenden Zutaten
  // (recipe_items). „Rezept" öffnet den bestehenden Rezept-Editor (Zutaten + Menge
  // → Food-Cost + Nährwerte); die Badge zeigt kcal/„Kein Rezept hinterlegt".
  // Bei Reuse (geliehen) wird das Rezept im Heim-Satz gepflegt → hier nur Badge.
  const recipeBtn = (ro || reuse) ? '' :
    `<button type="button" class="og-mini" title="verknüpfte Zutat bearbeiten (Nährwerte/Allergene aus der Warenwirtschaft)" onclick="ogItemRecipe('${it.id}')">🥗 Zutat</button>`;
  const tools = ro ? '' : `
      <span class="og-item-tools">
        ${recipeBtn}
        <button type="button" class="og-mini" title="hoch" onclick="ogItemMove('${it.id}', '${group.id}', 'up')">↑</button>
        <button type="button" class="og-mini" title="runter" onclick="ogItemMove('${it.id}', '${group.id}', 'down')">↓</button>
        ${reuse
          ? `<button type="button" class="og-mini og-danger" title="aus diesem Satz lösen" onclick="ogItemDetach('${it.id}', '${group.id}')">✕</button>`
          : `<button type="button" class="og-mini og-danger" title="Wahl-Item löschen" onclick="ogItemRemove('${it.id}', '${group.id}')">🗑</button>`}
      </span>`;
  return `<div class="og-item${dim}" data-oi-id="${it.id}">
      <div class="og-item-main">${badge}${nameInput}${priceInput}${avail}${tools}</div>
      <div class="og-nutri" id="recipe-nutri-badge-${it.id}"></div>
    </div>`;
}

// S206 — Inline-Create-Bausteine ------------------------------------------------

/** Zutat-Picker (Single-Select, optional). Wiederverwendet den Zutaten-Cache
 *  (_ingredients) — keine Duplizierung. `attrs` = fertiger onchange-Handler-String. */
function _ingredientSelectHtml(cls, selectedId, attrs) {
  const opts = _ingredients.map(i =>
    `<option value="${htmlEscape(i.id)}"${i.id === selectedId ? ' selected' : ''}>${htmlEscape(i.name || '')}</option>`).join('');
  return `<select class="${cls}" ${attrs || ''}><option value="">— Zutat (optional) —</option>${opts}</select>`;
}

/** S208 — Basiseinheit-Label der gewählten Zutat als Mengen-Suffix ('piece' → 'Stk'). */
function _ingredientBaseUnitLabel(id) {
  const ing = _ingredients.find(i => i.id === id);
  const u = ing && ing.baseUnit;
  if (!u) return '';
  return u === 'piece' ? 'Stk' : u;
}

/** S208 — optionales Mengen-Feld (aktiv nur mit gewählter Zutat) + Einheit-Suffix.
 *  `key`-Handler-String setzt das qty-Feld je Kontext (Fließband vs. Satz-Panel). */
function _qtyFieldHtml(qty, ingredientId, oninput, onkeydown) {
  return `<span class="og-item-qty-wrap" title="Menge in der Basiseinheit der Zutat">
      <input class="og-item-qty" type="number" step="0.1" min="0" value="${htmlEscape(String(qty != null ? qty : '1'))}"
        ${ingredientId ? '' : 'disabled'} oninput="${oninput}"${onkeydown ? ` onkeydown="${onkeydown}"` : ''}>
      <span class="og-item-qty-unit">${ingredientId ? htmlEscape(_ingredientBaseUnitLabel(ingredientId)) : ''}</span>
    </span>`;
}

/** Fließband-Zeile am Ende der Gruppe (Name · Zutat · Aufpreis · verfügbar). Leer,
 *  wenn kein Draft für diese Gruppe aktiv. */
function _itemDraftRowHtml(groupId) {
  if (!_itemDraft || _itemDraft.groupId !== groupId) return '';
  const d = _itemDraft;
  return `<div class="og-item og-item-draft" data-draft-group="${htmlEscape(groupId)}">
      <div class="og-item-main">
        <input class="og-item-name og-draft-name" type="text" value="${htmlEscape(d.name || '')}" placeholder="Name des Wahl-Items"
          oninput="ogItemDraftField('name', this.value)" onkeydown="ogItemDraftKey(event, '${htmlEscape(groupId)}')">
        ${_ingredientSelectHtml('og-draft-ing', d.ingredientId, `onchange="ogItemDraftIngredient('${htmlEscape(groupId)}', this.value)"`)}
        ${_qtyFieldHtml(d.qty, d.ingredientId, "ogItemDraftField('qty', this.value)", `ogItemDraftKey(event, '${htmlEscape(groupId)}')`)}
        <span class="og-item-price-wrap">+<input class="og-item-price" type="number" step="0.10" min="0"
          value="${htmlEscape(String(d.price != null ? d.price : '0'))}" oninput="ogItemDraftField('price', this.value)"
          onkeydown="ogItemDraftKey(event, '${htmlEscape(groupId)}')"> €</span>
        <label class="og-item-avail" title="verfügbar"><input type="checkbox" ${d.available !== false ? 'checked' : ''}
          onchange="ogItemDraftField('available', this.checked)"> verfügbar</label>
        <span class="og-item-tools">
          <button type="button" class="og-mini og-draft-save" title="Anlegen (Enter)" onclick="ogItemDraftSave('${htmlEscape(groupId)}')">✓ Anlegen</button>
          <button type="button" class="og-mini" title="Abbrechen (Esc)" onclick="ogItemDraftCancel()">✕</button>
        </span>
      </div>
    </div>`;
}

/** Eine Item-Zeile im Options-Satz-Panel (Name · Zutat · Aufpreis). */
function _groupDraftRowHtml(row, idx) {
  return `<div class="og-item og-draft-row" data-draft-idx="${idx}">
      <div class="og-item-main">
        <input class="og-item-name" type="text" value="${htmlEscape(row.name || '')}" placeholder="Option ${idx + 1} (leer = ignoriert)"
          oninput="ogGroupDraftRowField(${idx}, 'name', this.value)">
        ${_ingredientSelectHtml('og-draft-ing', row.ingredientId, `onchange="ogGroupDraftRowIngredient(${idx}, this.value)"`)}
        ${_qtyFieldHtml(row.qty, row.ingredientId, `ogGroupDraftRowField(${idx}, 'qty', this.value)`, '')}
        <span class="og-item-price-wrap">+<input class="og-item-price" type="number" step="0.10" min="0"
          value="${htmlEscape(String(row.price != null ? row.price : '0'))}" oninput="ogGroupDraftRowField(${idx}, 'price', this.value)"> €</span>
      </div>
    </div>`;
}

/** Inline-Panel „neuer Options-/Extra-Satz" (Name + Sofort-Items). Leer, wenn kein
 *  Draft für dieses kind aktiv. */
function _groupDraftPanelHtml(kind) {
  if (!_groupDraft || _groupDraft.kind !== kind) return '';
  const d = _groupDraft;
  const label = kind === 'extra' ? 'Extra' : 'Options';
  const rows = d.rows.map((r, i) => _groupDraftRowHtml(r, i)).join('');
  return `<div class="og-group-draft" id="og-group-draft-${kind}">
      <div class="og-group-draft-head">
        <strong>Neuer ${label}-Satz</strong>
        <input class="og-name og-draft-setname" type="text" value="${htmlEscape(d.name || '')}" placeholder="Satz-Name (z. B. Brötchen)"
          oninput="ogGroupDraftName(this.value)" onkeydown="ogGroupDraftKey(event, '${kind}')">
      </div>
      <div class="og-group-draft-hint hint">Optionen direkt anlegen (Zutat + Aufpreis optional); leere Zeilen werden ignoriert.</div>
      <div class="og-group-draft-rows">${rows}</div>
      <div class="og-group-draft-tools">
        <button type="button" class="btn btn-ghost btn-sm" onclick="ogGroupDraftAddRow('${kind}')">+ weitere Option</button>
        <button type="button" class="btn btn-primary btn-sm" onclick="ogGroupDraftSave('${kind}')">Satz anlegen</button>
        <button type="button" class="btn btn-ghost btn-sm" onclick="ogGroupDraftCancel()">Abbrechen</button>
      </div>
    </div>`;
}

/** Reuse-Kandidaten-Dropdown (nur option-Gruppen): option-Items, die NICHT bereits
 *  in dieser Gruppe (home/reuse) sind, gruppiert nach Heim-Label. */
function _reusePickerHtml(group) {
  if (group.kind !== 'option') return '';
  const present = new Set(itemsForGroup(_menu, group).map(x => x.item.id));
  const cands = _menu
    .filter(m => m && !m.deletedAt && m.itemType === 'option' && !present.has(m.id))
    .sort((a, b) => String(a.subcategory || '').localeCompare(String(b.subcategory || '')) || String(a.name).localeCompare(String(b.name)));
  if (!cands.length) return '';
  const opts = cands.map(m =>
    `<option value="${m.id}">${htmlEscape(m.name)}${m.subcategory ? ' — ' + htmlEscape(m.subcategory) : ''}</option>`).join('');
  return `<select class="og-reuse" onchange="ogReuseAdd('${group.id}', this.value)" title="Bestehende Option in diesen Satz übernehmen (Mehrfach-Zugehörigkeit)">
      <option value="">↔ vorhandene Option übernehmen…</option>${opts}</select>`;
}

function _groupCardHtml(group, ro) {
  const entries = itemsForGroup(_menu, group);
  const off = group.active ? '' : ' og-group-off';
  const items = entries.length
    ? entries.map(e => _itemRowHtml(group, e, ro)).join('')
    : `<div class="og-empty">Noch keine Wahl-Items.</div>`;
  // S193b — Regeln (Pflicht/Mehrfach/min/max) entscheidet das GERICHT je Satz
  // (menu_items.meta.optionSets, im Gericht-Konfigurator), NICHT die Gruppe.
  // Die Gruppe liefert nur Items + Identität (Rolle/Name). Kein config-Control mehr.
  const rules = `<span class="og-rule-hint hint" title="Pflicht/Mehrfach/min/max werden pro Gericht im Gericht-Konfigurator gesetzt">Regeln: pro Gericht</span>`;
  const open = _expandedGroups.has(group.id);   // S217 Cut 1 — Default eingeklappt
  const head = `
    <div class="og-group-head">
      <button type="button" class="og-collapse" data-ogid="${group.id}" title="${open ? 'einklappen' : 'aufklappen'}"
        aria-expanded="${open ? 'true' : 'false'}" onclick="ogToggleGroup(this)">${open ? '▾' : '▸'}</button>
      <input class="og-name" type="text" value="${htmlEscape(group.name || '')}" ${ro ? 'disabled' : ''}
        oninput="ogGroupRename('${group.id}', this.value)" placeholder="Satz-Name">
      ${_roleSelectHtml(group, ro)}
      ${rules}
      <label class="og-rule" title="aktiv"><input type="checkbox" ${group.active ? 'checked' : ''} ${ro ? 'disabled' : ''}
        onchange="ogGroupActive('${group.id}', this.checked)"> aktiv</label>
      <span class="og-count">${entries.length}</span>
      ${ro ? '' : `<span class="og-group-tools">
        <button type="button" class="og-mini" title="hoch" onclick="ogGroupMove('${group.id}', 'up')">↑</button>
        <button type="button" class="og-mini" title="runter" onclick="ogGroupMove('${group.id}', 'down')">↓</button>
        <button type="button" class="og-mini og-danger" title="Satz löschen" onclick="ogGroupRemove('${group.id}')">🗑</button>
      </span>`}
    </div>`;
  const add = ro ? '' : `
    <div class="og-item-add" id="og-item-add-${group.id}">
      ${_itemDraftRowHtml(group.id)}
      <div class="og-item-add-btns">
        <button type="button" class="btn btn-secondary btn-sm" onclick="ogItemAdd('${group.id}')">+ Wahl-Item</button>
        ${_reusePickerHtml(group)}
      </div>
    </div>`;
  // S217 Cut 1 — Body (Wahl-Items + Anlege-Bereich) erst nach Aufklappen im DOM.
  const body = open ? `
      <div class="og-items" id="og-items-${group.id}">${items}</div>
      ${add}` : '';
  return `<div class="og-group${off}${open ? ' is-open' : ''}" data-og-id="${group.id}">
      ${head}
      ${body}
    </div>`;
}

function _sectionHtml(kind, ro) {
  const sec = KIND_SECTIONS.find(s => s.kind === kind);
  const groups = listOptionGroups(_cats).filter(g => g.kind === kind);
  const cards = groups.length
    ? groups.map(g => _groupCardHtml(g, ro)).join('')
    : `<div class="og-empty">Noch keine ${htmlEscape(sec.title)}.</div>`;
  const addArea = ro ? '' : `<div class="og-add-area" id="og-add-area-${kind}" style="margin-top:10px;">${
    (_groupDraft && _groupDraft.kind === kind)
      ? _groupDraftPanelHtml(kind)
      : `<button type="button" class="btn btn-secondary btn-sm" onclick="ogGroupAdd('${kind}')">${htmlEscape(sec.addLabel)}</button>`
  }</div>`;
  return `<div class="card og-section" style="margin-top:14px;">
      <div class="og-section-head">
        <h3 style="margin:0;">${htmlEscape(sec.title)}</h3>
        <span class="hint">${htmlEscape(sec.hint)}</span>
      </div>
      ${cards}
      ${addArea}
    </div>`;
}

function _paneInner(ro) {
  return KIND_SECTIONS.map(s => _sectionHtml(s.kind, ro)).join('');
}

/* -------------------------------------------------------------------------- */
/* Render + partielle Re-Renders                                               */
/* -------------------------------------------------------------------------- */

export async function renderOptionGroupsAdmin() {
  const tab = document.getElementById('tab-option-groups');
  if (!tab) return;
  if (!flagOn()) { tab.innerHTML = ''; return; }
  tab.innerHTML = `<div class="card"><p class="hint">Lade Optionssätze…</p></div>`;
  try {
    [_cats, _ingredients] = await Promise.all([
      storage().menuCategories.list(),
      storage().ingredients.list().catch(() => [])   // S206 — Zutaten für den Inline-Picker
    ]);
  } catch (e) { console.warn('[optionsets] load:', e); _cats = []; _ingredients = _ingredients || []; }
  _menu = menuState();
  const ro = !canEdit();
  tab.innerHTML = `
    <div class="card">
      <h2 style="margin:0;">Optionssätze</h2>
      <p class="hint" style="margin:4px 0 0;">Auswahl- und Extra-Sätze, aus denen der Gericht-Konfigurator pickt.
      Rolle und Regeln (Pflicht/Mehrfach/min/max) leben hier an der Gruppe; Wahl-Items bekommen ihre
      Zuordnung automatisch — kein Meta-Gefummel.${ro ? ' <strong>Nur-Lese-Ansicht.</strong>' : ''}</p>
    </div>
    <div id="og-pane">${_paneInner(ro)}</div>`;
  _fillNutri(_allItemIds());
}

function _renderPane() {
  const pane = document.getElementById('og-pane');
  if (pane) { pane.innerHTML = _paneInner(!canEdit()); _fillNutri(_allItemIds()); }
}
function _renderGroupItems(groupId) {
  const el = document.getElementById('og-items-' + groupId);
  const group = _group(groupId);
  if (!el || !group) { _renderPane(); return; }
  const ro = !canEdit();
  const entries = itemsForGroup(_menu, group);
  el.innerHTML = entries.length ? entries.map(e => _itemRowHtml(group, e, ro)).join('') : `<div class="og-empty">Noch keine Wahl-Items.</div>`;
  const cnt = el.closest('.og-group') && el.closest('.og-group').querySelector('.og-count');
  if (cnt) cnt.textContent = String(entries.length);
  _fillNutri(entries.map(e => e.item.id));
}
/** Ganze Gruppen-Karte neu (Items + Zähler + Reuse-Picker) — bei Mitgliedschafts-
 *  Änderungen (add/remove/detach/reuse), damit die Kandidatenliste frisch ist.
 *  Wird nur durch bewusste Button/Select-Aktionen ausgelöst (kein Text-Fokus aktiv). */
function _renderGroupCard(groupId) {
  const card = document.querySelector('.og-group[data-og-id="' + groupId + '"]');
  const group = _group(groupId);
  if (!card || !group) { _renderPane(); return; }
  card.outerHTML = _groupCardHtml(group, !canEdit());
  _fillNutri(itemsForGroup(_menu, group).map(x => x.item.id));
}
async function _reload() {
  try { _cats = await storage().menuCategories.list(); } catch (e) { console.warn('[optionsets] reload:', e); }
  _menu = menuState();
  _renderPane();
}

/** S217 Cut 1 — Options-/Extra-Satz auf-/zuklappen (Modul-State). Liest die id
 *  aus dem data-Attribut des Carets; nur die betroffene Karte neu zeichnen. */
export function ogToggleGroup(el) {
  const id = el && el.dataset ? el.dataset.ogid : null;
  if (!id) return;
  if (_expandedGroups.has(id)) _expandedGroups.delete(id);
  else _expandedGroups.add(id);
  _renderGroupCard(id);
}

/* -------------------------------------------------------------------------- */
/* Gruppen-Handler                                                             */
/* -------------------------------------------------------------------------- */

export function ogGroupRename(id, value) {
  const g = _cats.find(c => c.id === id);
  if (!g) return;
  g.name = value;   // lokal (kein Re-Render → Fokus bleibt)
  _debouncedSave('gname:' + id, async () => {
    const name = (value || '').trim();
    if (!name) return;
    try { await storage().menuCategories.update(id, { name }); }
    catch (e) { showToast('Umbenennen fehlgeschlagen: ' + (e.message || e), 'error'); }
  });
}

export async function ogGroupRole(id, value) {
  if (!requireEditPermission('menu', 'Rolle ändern')) return;
  if (!OPTION_ROLES.includes(value)) return;
  const g = _cats.find(c => c.id === id); if (g) g.role = value;
  try { await storage().menuCategories.update(id, { role: value }); }
  catch (e) { showToast('Rolle speichern fehlgeschlagen: ' + (e.message || e), 'error'); }
}

// S193b — ogGroupRule entfernt: Optionsregeln (Pflicht/Mehrfach/min/max) entscheidet
// das GERICHT je Satz (meta.optionSets, Gericht-Konfigurator), nicht die Gruppe.
// menu_categories.config wird von der Gruppe nicht mehr geschrieben.

export async function ogGroupActive(id, checked) {
  if (!requireEditPermission('menu', 'Satz aktiv/inaktiv')) return;
  const cat = _cats.find(c => c.id === id); if (cat) cat.active = !!checked;
  try { await storage().menuCategories.update(id, { active: !!checked }); }
  catch (e) { showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error'); }
  _renderPane();
}

export async function ogGroupMove(id, dir) {
  if (!requireEditPermission('menu', 'Satz sortieren')) return;
  const g = _group(id); if (!g) return;
  const sibs = listOptionGroups(_cats).filter(x => x.kind === g.kind).map(x => x.id);
  const i = sibs.indexOf(id);
  const j = dir === 'up' ? i - 1 : i + 1;
  if (i < 0 || j < 0 || j >= sibs.length) return;
  sibs.splice(j, 0, sibs.splice(i, 1)[0]);
  sibs.forEach((sid, k) => { const c = _cats.find(x => x.id === sid); if (c) c.sortOrder = (k + 1) * 10; });
  _renderPane();
  try { await storage().menuCategories.reorder(sibs); } catch (e) { showToast('Sortieren fehlgeschlagen: ' + (e.message || e), 'error'); await _reload(); }
}

/** Nur die Add-Area einer Sektion neu zeichnen (Panel ein/aus/Zeile+). */
function _renderGroupDraftArea(kind) {
  const el = document.getElementById('og-add-area-' + kind);
  if (!el) { _renderPane(); return; }
  const sec = KIND_SECTIONS.find(s => s.kind === kind);
  el.innerHTML = (_groupDraft && _groupDraft.kind === kind)
    ? _groupDraftPanelHtml(kind)
    : `<button type="button" class="btn btn-secondary btn-sm" onclick="ogGroupAdd('${kind}')">${htmlEscape(sec.addLabel)}</button>`;
}
function _focusGroupDraftName(kind) {
  const el = document.querySelector(`#og-group-draft-${kind} .og-draft-setname`);
  if (el) el.focus();
}

// S206 — „+ Options-Satz"/„+ Extra-Satz" öffnet ein INLINE-Panel mit Sofort-Items.
export function ogGroupAdd(kind) {
  if (!requireEditPermission('menu', 'Optionssatz anlegen')) return;
  const root = rootForKind(_cats, kind);
  if (!root) { showToast('Kein Container „' + (kind === 'extra' ? 'Extras' : 'Optionen') + '" gefunden.', 'error'); return; }
  _itemDraft = null;   // nicht zwei Drafts gleichzeitig
  _groupDraft = { kind, name: '', rows: [{ name: '', ingredientId: '', qty: '1', price: '0' }] };
  _renderGroupDraftArea(kind);
  _focusGroupDraftName(kind);
}

export function ogGroupDraftName(value) { if (_groupDraft) _groupDraft.name = value; }
export function ogGroupDraftRowField(idx, field, value) {
  if (_groupDraft && _groupDraft.rows[idx]) _groupDraft.rows[idx][field] = value;
}
/** S208 — Zutat einer Panel-Zeile gewählt: State + Panel neu rendern (Mengen-Feld
 *  aktiviert), Fokus aufs Mengen-Feld dieser Zeile. Draft überlebt (Modul-State). */
export function ogGroupDraftRowIngredient(idx, value) {
  if (!_groupDraft || !_groupDraft.rows[idx]) return;
  _groupDraft.rows[idx].ingredientId = value;
  const kind = _groupDraft.kind;
  _renderGroupDraftArea(kind);
  const rows = document.querySelectorAll(`#og-group-draft-${kind} .og-draft-row`);
  const q = rows[idx] && rows[idx].querySelector('.og-item-qty');
  if (q && !q.disabled) { q.focus(); q.select(); }
}
export function ogGroupDraftKey(e, kind) {
  if (e && e.key === 'Escape') { e.preventDefault(); ogGroupDraftCancel(); }
}
export function ogGroupDraftAddRow(kind) {
  if (!_groupDraft || _groupDraft.kind !== kind) return;
  _groupDraft.rows.push({ name: '', ingredientId: '', qty: '1', price: '0' });
  _renderGroupDraftArea(kind);
  const rows = document.querySelectorAll(`#og-group-draft-${kind} .og-draft-row .og-item-name`);
  if (rows.length) rows[rows.length - 1].focus();
}
export function ogGroupDraftCancel() {
  const kind = _groupDraft && _groupDraft.kind;
  _groupDraft = null;
  if (kind) _renderGroupDraftArea(kind);
}

/** Satz + alle befüllten Items in EINEM Zug anlegen (sequenziell). Teil-Fehler:
 *  Satz bleibt, Toast nennt die fehlgeschlagenen Items. */
export async function ogGroupDraftSave(kind) {
  if (!_groupDraft || _groupDraft.kind !== kind) return;
  if (!requireEditPermission('menu', 'Optionssatz anlegen')) return;
  const root = rootForKind(_cats, kind);
  if (!root) { showToast('Kein Container gefunden.', 'error'); return; }
  const name = (_groupDraft.name || '').trim();
  if (!name) { _focusGroupDraftName(kind); return; }
  const filled = filterFilledRows(_groupDraft.rows);
  const existingKeys = _cats.map(c => c.groupKey).filter(Boolean);
  const groupKey = slugifyGroupKey(name, existingKeys);
  const sibs = listOptionGroups(_cats).filter(g => g.kind === kind);
  const { role, config } = groupKindDefaults(kind);
  let created;
  try {
    created = await storage().menuCategories.create({
      name, kind, parentId: root.id, sortOrder: (sibs.length + 1) * 10, active: true, role, config, groupKey
    });
  } catch (e) { showToast('Satz anlegen fehlgeschlagen: ' + (e.message || e), 'error'); return; }
  // Gruppe frisch aus dem Cache holen (Group-Shape für groupItemPayload).
  _cats = await storage().menuCategories.list().catch(() => _cats);
  const group = _group((created && created.id) || '') || _group(groupKey);
  const failed = [];
  if (group) {
    for (let i = 0; i < filled.length; i++) {
      const r = filled[i];
      try {
        const payload = groupItemPayload(group, {
          name: (r.name || '').trim(), price: Number(r.price) || 0, available: true,
          displayOrder: (i + 1) * 10, meta: {}
        });
        const it = await storage().menu.create(payload);
        if (r.ingredientId && it && it.id) {
          await storage().recipes.setItems(it.id, [cleanIngredientRow({ ingredientId: r.ingredientId, quantity: r.qty })].filter(Boolean));
        }
      } catch (_) { failed.push((r.name || '').trim() || ('Option ' + (i + 1))); }
    }
  }
  _groupDraft = null;
  showToast(failed.length
    ? `Satz „${name}" angelegt — fehlgeschlagen: ${failed.join(', ')}`
    : `Satz „${name}" angelegt`, failed.length ? 'error' : 'success');
  await _reload();
}

export async function ogGroupRemove(id) {
  if (!requireEditPermission('menu', 'Satz löschen')) return;
  const g = _group(id); if (!g) return;
  if (!(await window.styledConfirm({ title: `Optionssatz „${g.name}" löschen?`, danger: true, confirmLabel: 'Löschen' })).confirmed) return;
  try {
    const res = await storage().menuCategories.remove(id);
    showToast(res && res.deactivated ? 'Satz deaktiviert (hat noch Wahl-Items)' : 'Satz gelöscht', 'success');
    await _reload();
  } catch (e) { showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error'); }
}

/* -------------------------------------------------------------------------- */
/* Wahl-Item-Handler                                                           */
/* -------------------------------------------------------------------------- */

export function ogItemRename(id, value) {
  const it = _item(id); if (it) it.name = value;
  _debouncedSave('iname:' + id, async () => {
    const name = (value || '').trim(); if (!name) return;
    try { await storage().menu.update(id, { name }); }
    catch (e) { showToast('Umbenennen fehlgeschlagen: ' + (e.message || e), 'error'); }
  });
}

export function ogItemPrice(id, value) {
  const it = _item(id); if (it) it.price = Number(value) || 0;
  _debouncedSave('iprice:' + id, async () => {
    if (!requireEditPermission('menu', 'Aufpreis ändern')) return;
    try { await storage().menu.update(id, { price: Number(value) || 0 }); }
    catch (e) { showToast('Preis speichern fehlgeschlagen: ' + (e.message || e), 'error'); }
  });
}

export async function ogItemAvailable(id, groupId, checked) {
  if (!requireEditPermission('menu', 'Verfügbarkeit ändern')) return;
  const it = _item(id); if (it) it.available = !!checked;
  try { await storage().menu.update(id, { available: !!checked }); }
  catch (e) { showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error'); }
  _menu = menuState();
  _renderGroupItems(groupId);
}

export async function ogItemMove(id, groupId, dir) {
  if (!requireEditPermission('menu', 'Wahl-Item sortieren')) return;
  const group = _group(groupId); if (!group) return;
  const ids = itemsForGroup(_menu, group).map(x => x.item.id);
  const i = ids.indexOf(id);
  const j = dir === 'up' ? i - 1 : i + 1;
  if (i < 0 || j < 0 || j >= ids.length) return;
  ids.splice(j, 0, ids.splice(i, 1)[0]);
  ids.forEach((iid, k) => { const it = _menu.find(m => m.id === iid); if (it) it.displayOrder = (k + 1) * 10; });
  _renderGroupItems(groupId);
  try { await storage().menu.reorder(ids); } catch (e) { showToast('Sortieren fehlgeschlagen: ' + (e.message || e), 'error'); _menu = menuState(); _renderGroupItems(groupId); }
}

/** Fokus auf das Draft-Namensfeld der Gruppe (nach Re-Render). */
function _focusItemDraft(groupId) {
  const el = document.querySelector(`#og-item-add-${groupId} .og-draft-name`);
  if (el) { el.focus(); }
}

// S206 — „+ Wahl-Item" öffnet eine INLINE-Fließband-Zeile (kein styledPrompt mehr).
export function ogItemAdd(groupId) {
  if (!requireEditPermission('menu', 'Wahl-Item anlegen')) return;
  const group = _group(groupId); if (!group) return;
  _groupDraft = null;   // nicht zwei Drafts gleichzeitig
  _itemDraft = { groupId, name: '', ingredientId: '', qty: '1', price: '0', available: true };
  _expandedGroups.add(groupId);   // S217 Cut 1 — Fließband-Zeile liegt im Body → aufklappen
  _renderGroupCard(groupId);
  _focusItemDraft(groupId);
}

/** Draft-Feld live in den Modul-State (kein Re-Render → Fokus bleibt). */
export function ogItemDraftField(field, value) {
  if (!_itemDraft) return;
  _itemDraft[field] = value;
}

/** S208 — Zutat gewählt: State setzen + Zeile neu rendern (Mengen-Feld aktiviert +
 *  Einheit-Suffix; Draft überlebt als Modul-State) und Fokus aufs Mengen-Feld. */
export function ogItemDraftIngredient(groupId, value) {
  if (!_itemDraft || _itemDraft.groupId !== groupId) return;
  _itemDraft.ingredientId = value;
  _renderGroupCard(groupId);
  const q = document.querySelector(`#og-item-add-${groupId} .og-item-qty`);
  if (q && !q.disabled) { q.focus(); q.select(); }
}

/** Enter = anlegen, Escape = abbrechen (CSP-konform statt Inline-if). */
export function ogItemDraftKey(e, groupId) {
  if (!e) return;
  if (e.key === 'Enter') { e.preventDefault(); ogItemDraftSave(groupId); }
  else if (e.key === 'Escape') { e.preventDefault(); ogItemDraftCancel(); }
}

export function ogItemDraftCancel() {
  const gid = _itemDraft && _itemDraft.groupId;
  _itemDraft = null;
  if (gid) _renderGroupCard(gid);
}

/** Anlegen inkl. optionalem Zutat-Link in EINEM Zug; danach leert sich die Zeile
 *  für das nächste Item (Fließband). Save-Fehler → Toast, Eingaben bleiben stehen. */
export async function ogItemDraftSave(groupId) {
  if (!_itemDraft || _itemDraft.groupId !== groupId) return;
  if (!requireEditPermission('menu', 'Wahl-Item anlegen')) return;
  const group = _group(groupId); if (!group) return;
  const name = (_itemDraft.name || '').trim();
  if (!name) { _focusItemDraft(groupId); return; }
  const ingredientId = _itemDraft.ingredientId || '';
  const qty = _itemDraft.qty;   // S208 — vor dem await sichern (Draft kann sich ändern)
  const maxOrder = itemsForGroup(_menu, group).reduce((mx, x) => Math.max(mx, Number(x.item.displayOrder) || 0), 0);
  const payload = groupItemPayload(group, {
    name, price: Number(_itemDraft.price) || 0, available: _itemDraft.available !== false,
    displayOrder: maxOrder + 10, meta: {}
  });
  try {
    const created = await storage().menu.create(payload);
    if (ingredientId && created && created.id) {
      const row = cleanIngredientRow({ ingredientId, quantity: qty });
      try { await storage().recipes.setItems(created.id, [row].filter(Boolean)); }
      catch (re) { showToast('Item angelegt, Zutat-Link fehlgeschlagen: ' + (re.message || re), 'error'); }
    }
    _menu = menuState();
    // Fließband: Zeile leeren (gleiche Gruppe) fürs nächste Item.
    _itemDraft = { groupId, name: '', ingredientId: '', qty: '1', price: '0', available: true };
    _renderGroupCard(groupId);
    _focusItemDraft(groupId);
  } catch (e) { showToast('Anlegen fehlgeschlagen: ' + (e.message || e), 'error'); }
}

export async function ogItemRemove(id, groupId) {
  if (!requireEditPermission('menu', 'Wahl-Item löschen')) return;
  const it = _item(id); if (!it) return;
  if (!(await window.styledConfirm({ title: `Wahl-Item „${it.name}" löschen?`, danger: true, confirmLabel: 'Löschen' })).confirmed) return;
  try {
    await storage().menu.remove(id);
    _menu = menuState();
    _renderGroupCard(groupId);
  } catch (e) { showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error'); }
}

/** Reuse lösen: group_key aus optionGroups[] entfernen — Item bleibt im Heim-Satz. */
export async function ogItemDetach(id, groupId) {
  if (!requireEditPermission('menu', 'Reuse lösen')) return;
  const it = _item(id); const group = _group(groupId);
  if (!it || !group) return;
  try {
    await storage().menu.update(id, { meta: removeReuseFromMeta(it.meta, group.groupKey) });
    _menu = menuState();
    _renderGroupCard(groupId);
  } catch (e) { showToast('Lösen fehlgeschlagen: ' + (e.message || e), 'error'); }
}

/** Rezept/Zutaten eines Wahl-Items bearbeiten — öffnet den bestehenden Rezept-Editor
 *  (Zutaten + Menge → Food-Cost + Nährwerte). Nach dem Speichern aktualisiert dieser
 *  selbst die Nährwert-Badge (#recipe-nutri-badge-<id>) in dieser Zeile. */
export function ogItemRecipe(id) {
  if (!requireEditPermission('menu', 'Zutat bearbeiten')) return;
  if (typeof window.openRecipeEditor === 'function') window.openRecipeEditor(id);
}

/** Reuse hinzufügen: bestehende Option zusätzlich in diesen Satz (optionGroups[]). */
export async function ogReuseAdd(groupId, itemId) {
  if (!itemId) return;
  if (!requireEditPermission('menu', 'Option übernehmen')) return;
  const it = _item(itemId); const group = _group(groupId);
  if (!it || !group || group.kind !== 'option') return;
  try {
    await storage().menu.update(itemId, { meta: addReuseToMeta(it.meta, group.groupKey) });
    _menu = menuState();
    _renderGroupCard(groupId);
  } catch (e) { showToast('Übernehmen fehlgeschlagen: ' + (e.message || e), 'error'); }
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderOptionGroupsAdmin,
    ogToggleGroup,   // S217 Cut 1 — Satz auf-/zuklappen
    ogGroupRename, ogGroupRole, ogGroupActive, ogGroupMove, ogGroupAdd, ogGroupRemove,
    ogItemRename, ogItemPrice, ogItemAvailable, ogItemMove, ogItemAdd, ogItemRemove, ogItemDetach, ogReuseAdd,
    ogItemRecipe,
    // S206 — Inline-Create (Fließband-Zeile + Options-Satz-Panel)
    ogItemDraftField, ogItemDraftKey, ogItemDraftCancel, ogItemDraftSave,
    ogGroupDraftName, ogGroupDraftRowField, ogGroupDraftKey, ogGroupDraftAddRow, ogGroupDraftCancel, ogGroupDraftSave,
    // S208 — Zutat-Wahl aktiviert das Mengen-Feld (Re-Render + Fokus)
    ogItemDraftIngredient, ogGroupDraftRowIngredient
  });
}
